import React, { useState, useEffect } from 'react';
import { recruitJobsApis } from '../../Data/axios';

const jobOpenings = [
    {
        id: 1,
        title: "Frontend Developer",
        intro: "We are looking for a skilled developer to create engaging user interfaces.",
        expandedDescription: {
            line1: "Join our dynamic team to build responsive and user-friendly interfaces.",
            line2: "Work closely with designers to bring creative visions to life.",
            line3: "Leverage React.js to optimize performance and enhance user experience."
        },
        responsibilities: [
            "Develop and maintain responsive web applications using React.js.",
            "Collaborate with UX designers to implement visually appealing designs.",
            "Optimize applications for maximum speed and scalability."
        ],
        requirements: [
            "Bachelor's degree in Computer Science or related field.",
            "2+ years of experience with React.js and JavaScript.",
            "Strong understanding of responsive design principles."
        ],
        type: "Internship",
        location: "Remote",
        salary: "20,000/month"
    },
    {
        id: 2,
        title: "Backend Developer",
        intro: "We are looking for a talented developer to build scalable backend services.",
        expandedDescription: {
            line1: "Develop robust backend systems to support our growing applications.",
            line2: "Design and implement APIs using Node.js for seamless integration.",
            line3: "Optimize databases and ensure secure, efficient data handling."
        },
        responsibilities: [
            "Build and maintain APIs using Node.js and Express.",
            "Optimize database queries for performance.",
            "Implement secure authentication and authorization mechanisms."
        ],
        requirements: [
            "Bachelor's degree in Computer Science or related field.",
            "2+ years of experience with Node.js and databases (e.g., MongoDB).",
            "Knowledge of RESTful API design and security best practices."
        ],
        type: "Internship",
        location: "Remote",
        salary: "20,000/month"
    },
    {
        id: 3,
        title: "Freelance UI/UX Designer",
        intro: "We are looking for a creative designer for mobile app interfaces.",
        expandedDescription: {
            line1: "Design engaging and user-friendly mobile app interfaces.",
            line2: "Use Figma to prototype and iterate on user-centered designs.",
            line3: "Conduct user research to ensure an exceptional app experience."
        },
        responsibilities: [
            "Design intuitive UI/UX for mobile applications.",
            "Create wireframes and prototypes using Figma.",
            "Conduct user testing and gather feedback for iterations."
        ],
        requirements: [
            "Proven experience as a UI/UX designer (portfolio required).",
            "Proficiency in Figma and other design tools.",
            "Strong understanding of user-centered design principles."
        ],
        type: "Contract-Based",
        location: "Remote",
        salary: "₹5,000/month"
    },
    {
        id: 4,
        title: "Tele Caller (Female)",
        intro: "We are looking for a communicator to handle customer interactions.",
        expandedDescription: {
            line1: "Engage with customers through outbound calls to promote services.",
            line2: "Build strong relationships while addressing inquiries and concerns.",
            line3: "Maintain detailed call records for effective follow-ups and reporting."
        },
        responsibilities: [
            "Make outbound calls to promote company services.",
            "Handle customer inquiries and resolve issues promptly.",
            "Maintain accurate records of all interactions."
        ],
        requirements: [
            "Excellent communication skills in English and Hindi.",
            "Prior experience in telecalling or customer service preferred.",
            "Ability to work in a target-driven environment."
        ],
        type: "Full-time",
        location: "Lucknow",
        salary: "12,000/month"
    },
    {
        id: 5,
        title: "Accountant",
        intro: "We are looking for an experienced accountant to manage finances.",
        expandedDescription: {
            line1: "Oversee financial operations, ensuring accuracy in all transactions.",
            line2: "Handle taxation, compliance, and preparation of financial reports.",
            line3: "Maintain detailed records to support audits and decision-making."
        },
        responsibilities: [
            "Manage financial transactions and ensure compliance.",
            "Prepare monthly financial reports and tax filings.",
            "Assist in audits and maintain accurate records."
        ],
        requirements: [
            "Bachelor's degree in Accounting or Finance.",
            "2+ years of accounting experience.",
            "Proficiency in accounting software (e.g., Tally)."
        ],
        type: "Full-time",
        location: "Lucknow",
        salary: "15,000/month"
    },
    {
        id: 6,
        title: "Operation Manager",
        intro: "We are looking for a manager to oversee logistics and operations.",
        expandedDescription: {
            line1: "Lead logistics operations to ensure smooth and efficient workflows.",
            line2: "Manage drivers, optimize routes, and monitor delivery schedules.",
            line3: "Ensure compliance with regulations while improving operational efficiency."
        },
        responsibilities: [
            "Oversee daily logistics and operational activities.",
            "Optimize delivery routes and manage driver schedules.",
            "Ensure compliance with safety and regulatory standards."
        ],
        requirements: [
            "Bachelor's degree in Business or related field.",
            "3+ years of experience in operations management.",
            "Strong leadership and problem-solving skills."
        ],
        type: "Full-time",
        location: "Lucknow",
        salary: "30,000/month"
    }
];

const CareersBody = () => {
    // API state management
    const [jobOpeningsData, setJobOpeningsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [filter, setFilter] = useState('');
    const [jobTitleFilter, setJobTitleFilter] = useState('');
    const [jobTypeFilter, setJobTypeFilter] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [expandedJobs, setExpandedJobs] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [showApplicationForm, setShowApplicationForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        linkedin: '',
        experience: '',
        currentLocation: '',
        noticePeriod: '',
        coverLetter: '',
        resume: null
    });
    const [errors, setErrors] = useState({
        name: '',
        email: '',
        phone: '',
        linkedin: '',
        experience: '',
        currentLocation: '',
        noticePeriod: '',
        coverLetter: '',
        resume: ''
    });
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareJob, setShareJob] = useState(null);

    // Function to map API response to component structure
    const mapApiDataToJobStructure = (apiData) => {
        return apiData.map(job => ({
            id: job.id,
            title: job.title,
            intro: job.description || "Job description not available",
            expandedDescription: {
                line1: job.description || "Job description not available",
                line2: job.requirements || "Requirements not specified",
                line3: "For more details, please apply to this position."
            },
            responsibilities: job.requirements ? [job.requirements] : ["Responsibilities will be discussed during interview"],
            requirements: job.requirements ? [job.requirements] : ["Requirements will be discussed during interview"],
            type: job.job_type === 1 ? "Full-time" : job.job_type === 2 ? "Part-time" : "Contract-Based",
            location: job.remote === 1 ? "Remote" : "On-site",
            salary: job.salary === 1 ? 
                (job.min_salary && job.max_salary ? 
                    `₹${job.min_salary.toLocaleString()} - ₹${job.max_salary.toLocaleString()}/month` : 
                    (job.rate ? `₹${job.rate.toLocaleString()}/month` : "Salary not specified")) 
                : "Salary not specified",
            department: job.department?.department || "Not specified",
            experience: job.experience || 0,
            vacancy: job.vacancy || 1,
            recruiter: job.recruiter,
            created_at: job.created_at,
            start_date: job.start_date,
            end_date: job.end_date
        }));
    };

    // Function to fetch jobs from API
    const fetchJobs = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('Fetching jobs from API...');
            
            const response = await recruitJobsApis.getAllWeb();
            console.log('API response:', response);
            
            if (response && response.jobs && response.jobs.data && Array.isArray(response.jobs.data)) {
                console.log('Job data received:', response.jobs.data.length, 'jobs');
                const mappedJobs = mapApiDataToJobStructure(response.jobs.data);
                setJobOpeningsData(mappedJobs);
                setError(null);
            } else {
                console.error('Invalid response structure:', response);
                throw new Error('Invalid response structure from API');
            }
        } catch (err) {
            console.error('Error fetching jobs:', err.message, err);
            let errorMessage = 'Failed to load job openings. ';
            
            if (err.code === 'NETWORK_ERROR' || err.message.includes('Network Error')) {
                errorMessage += 'Please check your internet connection.';
            } else if (err.response && err.response.status) {
                errorMessage += `Server responded with status ${err.response.status}.`;
            } else if (err.message.includes('Invalid response structure')) {
                errorMessage += 'API returned unexpected data format.';
            } else {
                errorMessage += 'Please try again later.';
            }
            
            setError(errorMessage);
            // Fallback to static data in case of error
            console.log('Using fallback static data');
            setJobOpeningsData(jobOpenings);
        } finally {
            setLoading(false);
        }
    };

    // useEffect to fetch jobs on component mount
    useEffect(() => {
        fetchJobs();
    }, []);

    // Use API data if available, otherwise fallback to static data
    const currentJobData = jobOpeningsData.length > 0 ? jobOpeningsData : jobOpenings;

    // Map location to "Remote" or "On-site"
    const mapLocation = (location) => {
        return location === "Remote" ? "Remote" : "On-site";
    };

    // Filter jobs based on search bar or dropdowns
    const filteredJobs = currentJobData.filter(job => {
        const matchesSearch = filter
            ? job.title.toLowerCase().includes(filter.toLowerCase()) ||
              job.intro.toLowerCase().includes(filter.toLowerCase()) ||
              job.location.toLowerCase().includes(filter.toLowerCase())
            : true;

        const matchesJobTitle = jobTitleFilter
            ? job.title.toLowerCase() === jobTitleFilter.toLowerCase()
            : true;

        const matchesJobType = jobTypeFilter
            ? job.type.toLowerCase() === jobTypeFilter.toLowerCase()
            : true;

        const matchesLocation = locationFilter
            ? mapLocation(job.location).toLowerCase() === locationFilter.toLowerCase()
            : true;

        return matchesSearch && matchesJobTitle && matchesJobType && matchesLocation;
    });

    // Unique values for dropdowns
    const jobTitles = [...new Set(currentJobData.map(job => job.title))];
    const jobTypes = [...new Set(currentJobData.map(job => job.type))];
    const locations = [...new Set(currentJobData.map(job => mapLocation(job.location)))];

    const handleSearchChange = (e) => {
        setFilter(e.target.value);
        setIsTyping(e.target.value.length > 0);
    };

    const toggleJobSummary = (jobId) => {
        setExpandedJobs(prev => ({
            ...prev,
            [jobId]: !prev[jobId]
        }));
    };

    // Handle job card click to go to detailed page
    const handleJobCardClick = (job) => {
        setSelectedJob(job);
    };

    // Handle Apply Now button on homepage (redirects to detailed page)
    const handleApplyNow = (job) => {
        setSelectedJob(job);
    };

    // Handle Back to Listings
    const handleBackToListings = () => {
        setSelectedJob(null);
        setShowApplicationForm(false);
    };

    // Handle modal open/close for Upload Resume
    const openModal = () => setShowModal(true);
    const closeModal = () => {
        setShowModal(false);
        setFormData({
            name: '',
            email: '',
            phone: '',
            linkedin: '',
            experience: '',
            currentLocation: '',
            noticePeriod: '',
            coverLetter: '',
            resume: null
        });
        setErrors({
            name: '',
            email: '',
            phone: '',
            linkedin: '',
            experience: '',
            currentLocation: '',
            noticePeriod: '',
            coverLetter: '',
            resume: ''
        });
    };

    // Handle application form open/close
    const openApplicationForm = () => {
        setShowApplicationForm(true);
    };
    const closeApplicationForm = () => {
        setShowApplicationForm(false);
        setFormData({
            name: '',
            email: '',
            phone: '',
            linkedin: '',
            experience: '',
            currentLocation: '',
            noticePeriod: '',
            coverLetter: '',
            resume: null
        });
        setErrors({
            name: '',
            email: '',
            phone: '',
            linkedin: '',
            experience: '',
            currentLocation: '',
            noticePeriod: '',
            coverLetter: '',
            resume: ''
        });
    };

    // Input validation for application form
    const validateName = (name) => {
        const nameRegex = /^[A-Za-z\s]+$/;
        if (!name) return "Name is required.";
        if (!nameRegex.test(name)) return "Name should contain only letters and spaces.";
        return "";
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return "Email is required.";
        if (!emailRegex.test(email)) return "Please enter a valid email address.";
        return "";
    };

    const validatePhone = (phone) => {
        const phoneRegex = /^\+?\d{10,15}$/;
        if (!phone) return "Phone number is required.";
        if (!phoneRegex.test(phone)) return "Please enter a valid phone number (10-15 digits).";
        return "";
    };

    const validateExperience = (experience) => {
        const expRegex = /^\d+(\.\d+)?$/;
        if (!experience) return "Years of experience is required.";
        if (!expRegex.test(experience) || experience < 0) return "Please enter a valid number of years (e.g., 2 or 2.5).";
        return "";
    };

    const validateCurrentLocation = (currentLocation) => {
        if (!currentLocation) return "Current location is required.";
        return "";
    };

    const validateNoticePeriod = (noticePeriod) => {
        const noticeRegex = /^\d+$/;
        if (!noticePeriod) return "Notice period is required.";
        if (!noticeRegex.test(noticePeriod) || noticePeriod < 0) return "Please enter a valid number of days.";
        return "";
    };

    const validateCoverLetter = (coverLetter) => {
        if (!coverLetter) return "Cover letter is required.";
        return "";
    };

    const validateResume = (file) => {
        if (!file) return "Please upload a resume.";
        const allowedTypes = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (!allowedTypes.includes(file.type)) return "Only .pdf and .docx files are allowed.";
        if (file.size > maxSize) return "File size must be less than 10MB.";
        return "";
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Sanitize input to prevent XSS
        const sanitizedValue = value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
        setFormData(prev => ({ ...prev, [name]: sanitizedValue }));

        if (name === "name") {
            setErrors(prev => ({ ...prev, name: validateName(sanitizedValue) }));
        } else if (name === "email") {
            setErrors(prev => ({ ...prev, email: validateEmail(sanitizedValue) }));
        } else if (name === "phone") {
            setErrors(prev => ({ ...prev, phone: validatePhone(sanitizedValue) }));
        } else if (name === "experience") {
            setErrors(prev => ({ ...prev, experience: validateExperience(sanitizedValue) }));
        } else if (name === "currentLocation") {
            setErrors(prev => ({ ...prev, currentLocation: validateCurrentLocation(sanitizedValue) }));
        } else if (name === "noticePeriod") {
            setErrors(prev => ({ ...prev, noticePeriod: validateNoticePeriod(sanitizedValue) }));
        } else if (name === "coverLetter") {
            setErrors(prev => ({ ...prev, coverLetter: validateCoverLetter(sanitizedValue) }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({ ...prev, resume: file }));
        setErrors(prev => ({ ...prev, resume: validateResume(file) }));
    };

    const handleApplicationSubmit = (e) => {
        e.preventDefault();
        const nameError = validateName(formData.name);
        const emailError = validateEmail(formData.email);
        const phoneError = validatePhone(formData.phone);
        const experienceError = validateExperience(formData.experience);
        const currentLocationError = validateCurrentLocation(formData.currentLocation);
        const noticePeriodError = validateNoticePeriod(formData.noticePeriod);
        const coverLetterError = validateCoverLetter(formData.coverLetter);
        const resumeError = validateResume(formData.resume);

        if (nameError || emailError || phoneError || experienceError || currentLocationError || noticePeriodError || coverLetterError || resumeError) {
            setErrors({
                name: nameError,
                email: emailError,
                phone: phoneError,
                experience: experienceError,
                currentLocation: currentLocationError,
                noticePeriod: noticePeriodError,
                coverLetter: coverLetterError,
                resume: resumeError
            });
            return;
        }

        // TODO: Submit form data to backend API
        // For now, just log the data
        console.log("Application Data:", { ...formData, jobId: selectedJob.id });
        
        // Show success message (you can replace this with actual API call)
        alert("Application submitted successfully! We will contact you soon.");
        closeApplicationForm();
    };

    // Helper to get current job URL (simulate for now)
    const getJobUrl = (job) => {
        return 'https://panel.roadpilot.co.in/careers';
    };

    // Share message
    const getShareMessage = (job) =>
        `Check out this job opportunity!\n\nPosition: ${job.title}\n\n${job.intro}\n\nApply here: ${getJobUrl(job)}\n\nShared via RoadPilot Careers Portal.`;

    // Share handlers
    const handleShareClick = (job, e) => {
        e.stopPropagation();
        setShareJob(job);
        setShowShareModal(true);
    };
    const closeShareModal = () => {
        setShowShareModal(false);
        setShareJob(null);
    };

    // Add share icon SVG (yellow)
    const shareIcon = (
        <svg fill="#f59e0b" height="20" width="20" viewBox="0 0 458.624 458.624" style={{ display: 'inline', verticalAlign: 'middle' }}>
            <g> <g> <path d="M339.588,314.529c-14.215,0-27.456,4.133-38.621,11.239l-112.682-78.67c1.809-6.315,2.798-12.976,2.798-19.871 c0-6.896-0.989-13.557-2.798-19.871l109.64-76.547c11.764,8.356,26.133,13.286,41.662,13.286c39.79,0,72.047-32.257,72.047-72.047 C411.634,32.258,379.378,0,339.588,0c-39.79,0-72.047,32.257-72.047,72.047c0,5.255,0.578,10.373,1.646,15.308l-112.424,78.491 c-10.974-6.759-23.892-10.666-37.727-10.666c-39.79,0-72.047,32.257-72.047,72.047s32.256,72.047,72.047,72.047 c13.834,0,26.753-3.907,37.727-10.666l113.292,79.097c-1.629,6.017-2.514,12.34-2.514,18.872c0,39.79,32.257,72.047,72.047,72.047 c39.79,0,72.047-32.257,72.047-72.047C411.635,346.787,379.378,314.529,339.588,314.529z"></path> </g> </g>
        </svg>
    );

    // Social icons
    const socialIcons = {
        facebook: (
            <svg width="24" height="24" fill="#1877F3" viewBox="0 0 24 24"><path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0"/></svg>
        ),
        whatsapp: (
            <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="28" height="28" viewBox="0 0 48 48">
                <path fill="#fff" d="M4.868,43.303l2.694-9.835C5.9,30.59,5.026,27.324,5.027,23.979C5.032,13.514,13.548,5,24.014,5c5.079,0.002,9.845,1.979,13.43,5.566c3.584,3.588,5.558,8.356,5.556,13.428c-0.004,10.465-8.522,18.98-18.986,18.98c-0.001,0,0,0,0,0h-0.008c-3.177-0.001-6.3-0.798-9.073-2.311L4.868,43.303z"></path>
                <path fill="#fff" d="M4.868,43.803c-0.132,0-0.26-0.052-0.355-0.148c-0.125-0.127-0.174-0.312-0.127-0.483l2.639-9.636c-1.636-2.906-2.499-6.206-2.497-9.556C4.532,13.238,13.273,4.5,24.014,4.5c5.21,0.002,10.105,2.031,13.784,5.713c3.679,3.683,5.704,8.577,5.702,13.781c-0.004,10.741-8.746,19.48-19.486,19.48c-3.189-0.001-6.344-0.788-9.144-2.277l-9.875,2.589C4.953,43.798,4.911,43.803,4.868,43.803z"></path>
                <path fill="#cfd8dc" d="M24.014,5c5.079,0.002,9.845,1.979,13.43,5.566c3.584,3.588,5.558,8.356,5.556,13.428c-0.004,10.465-8.522,18.98-18.986,18.98h-0.008c-3.177-0.001-6.3-0.798-9.073-2.311L4.868,43.303l2.694-9.835C5.9,30.59,5.026,27.324,5.027,23.979C5.032,13.514,13.548,5,24.014,5 M24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974 M24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974 M24.014,4C24.014,4,24.014,4,24.014,4C12.998,4,4.032,12.962,4.027,23.979c-0.001,3.367,0.849,6.685,2.461,9.622l-2.585,9.439c-0.094,0.345,0.002,0.713,0.254,0.967c0.19,0.192,0.447,0.297,0.711,0.297c0.085,0,0.17-0.011,0.254-0.033l9.687-2.54c2.828,1.468,5.998,2.243,9.197,2.244c11.024,0,19.99-8.963,19.995-19.98c0.002-5.339-2.075-10.359-5.848-14.135C34.378,6.083,29.357,4.002,24.014,4L24.014,4z"></path>
                <path fill="#40c351" d="M35.176,12.832c-2.98-2.982-6.941-4.625-11.157-4.626c-8.704,0-15.783,7.076-15.787,15.774c-0.001,2.981,0.833,5.883,2.413,8.396l0.376,0.597l-1.595,5.821l5.973-1.566l0.577,0.342c2.422,1.438,5.2,2.198,8.032,2.199h0.006c8.698,0,15.777-7.077,15.78-15.776C39.795,19.778,38.156,15.814,35.176,12.832z"></path>
                <path fill="#fff" fillRule="evenodd" d="M19.268,16.045c-0.355-0.79-0.729-0.806-1.068-0.82c-0.277-0.012-0.593-0.011-0.909-0.011c-0.316,0-0.83,0.119-1.265,0.594c-0.435,0.475-1.661,1.622-1.661,3.956c0,2.334,1.7,4.59,1.937,4.906c0.237,0.316,3.282,5.259,8.104,7.161c4.007,1.58,4.823,1.266,5.693,1.187c0.87-0.079,2.807-1.147,3.202-2.255c0.395-1.108,0.395-2.057,0.277-2.255c-0.119-0.198-0.435-0.316-0.909-0.554s-2.807-1.385-3.242-1.543c-0.435-0.158-0.751-0.237-1.068,0.238c-0.316,0.474-1.225,1.543-1.502,1.859c-0.277,0.317-0.554,0.357-1.028,0.119c-0.474-0.238-2.002-0.738-3.815-2.354c-1.41-1.257-2.362-2.81-2.639-3.285c-0.277-0.474-0.03-0.731,0.208-0.968c0.213-0.213,0.474-0.554,0.712-0.831c0.237-0.277,0.316-0.475,0.474-0.791c0.158-0.317,0.079-0.594-0.04-0.831C20.612,19.329,19.69,16.983,19.268,16.045z" clipRule="evenodd"></path>
            </svg>
        ),
        telegram: (
            <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="28" height="28" viewBox="0 0 48 48">
                <path fill="#29b6f6" d="M24 4A20 20 0 1 0 24 44A20 20 0 1 0 24 4Z"></path><path fill="#fff" d="M33.95,15l-3.746,19.126c0,0-0.161,0.874-1.245,0.874c-0.576,0-0.873-0.274-0.873-0.274l-8.114-6.733 l-3.97-2.001l-5.095-1.355c0,0-0.907-0.262-0.907-1.012c0-0.625,0.933-0.923,0.933-0.923l21.316-8.468 c-0.001-0.001,0.651-0.235,1.126-0.234C33.667,14,34,14.125,34,14.5C34,14.75,33.95,15,33.95,15z"></path><path fill="#b0bec5" d="M23,30.505l-3.426,3.374c0,0-0.149,0.115-0.348,0.12c-0.069,0.002-0.143-0.009-0.219-0.043 l0.964-5.965L23,30.505z"></path><path fill="#cfd8dc" d="M29.897,18.196c-0.169-0.22-0.481-0.26-0.701-0.093L16,26c0,0,2.106,5.892,2.427,6.912 c0.322,1.021,0.58,1.045,0.58,1.045l0.964-5.965l9.832-9.096C30.023,18.729,30.064,18.416,29.897,18.196z"></path>
            </svg>
        ),
        email: (
            <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24" height="24" viewBox="0 0 48 48">
                <path fill="#4caf50" d="M45,16.2l-5,2.75l-5,4.75L35,40h7c1.657,0,3-1.343,3-3V16.2z"></path>
                <path fill="#1e88e5" d="M3,16.2l3.614,1.71L13,23.7V40H6c-1.657,0-3-1.343-3-3V16.2z"></path>
                <polygon fill="#e53935" points="35,11.2 24,19.45 13,11.2 12,17 13,23.7 24,31.95 35,23.7 36,17"></polygon>
                <path fill="#c62828" d="M3,12.298V16.2l10,7.5V11.2L9.876,8.859C9.132,8.301,8.228,8,7.298,8h0C4.924,8,3,9.924,3,12.298z"></path>
                <path fill="#fbc02d" d="M45,12.298V16.2l-10,7.5V11.2l3.124-2.341C38.868,8.301,39.772,8,40.702,8h0 C43.076,8,45,9.924,45,12.298z"></path>
            </svg>
        )
    };

    // Responsive styles
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    const filterSectionStyle = {
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'center',
        flexDirection: isMobile ? 'column' : 'row',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
    };

    const dropdownContainerStyle = {
        position: 'relative',
        width: '100%',
        maxWidth: isMobile ? '100%' : '200px',
        minWidth: '200px'
    };

    const dropdownStyle = {
        padding: '0.75rem 2.5rem 0.75rem 1rem',
        border: '1px solid #e5e7eb',
        borderRadius: '0.75rem',
        backgroundColor: '#fff',
        fontSize: isMobile ? '0.9rem' : '1rem',
        color: '#6b7280',
        outline: 'none',
        cursor: isTyping ? 'not-allowed' : 'pointer',
        opacity: isTyping ? 0.5 : 1,
        width: '100%',
        appearance: 'none',
        overflowWrap: 'break-word'
    };

    const searchContainerStyle = {
        position: 'relative',
        width: '100%',
        maxWidth: isMobile ? '100%' : '200px',
        minWidth: '200px'
    };

    const searchInputStyle = {
        padding: '0.75rem 2.5rem 0.75rem 0',
        border: 'none',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: 'transparent',
        fontSize: isMobile ? '0.9rem' : '1rem',
        color: '#6b7280',
        outline: 'none',
        width: '100%',
        overflowWrap: 'break-word'
    };

    const jobCardStyle = {
        backgroundColor: '#fff',
        padding: isMobile ? '1.5rem' : '2rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb',
        transition: 'box-shadow 0.3s, transform 0.3s',
        width: '100%',
        cursor: 'pointer'
    };

    const buttonStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: '#f59e0b',
        color: '#fff',
        padding: '0.75rem 1.5rem',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        fontWeight: '500',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.3s'
    };

    // Modal styles (for Upload Resume)
    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    };

    const modalContentStyle = {
        backgroundColor: '#fff',
        padding: isMobile ? '1.5rem' : '2rem',
        borderRadius: '0.75rem',
        width: '100%',
        maxWidth: isMobile ? '90%' : '400px',
        position: 'relative',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
    };

    const closeButtonStyle = {
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        background: 'none',
        border: 'none',
        fontSize: '1.5rem',
        cursor: 'pointer',
        color: '#4b5563'
    };

    const inputStyle = {
        width: '100%',
        padding: '0.75rem 1rem',
        marginBottom: '0.5rem',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        outline: 'none',
        color: '#4b5563',
        transition: 'border-color 0.3s, box-shadow 0.3s',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        backgroundColor: '#fafafa'
    };

    const textareaStyle = {
        width: '100%',
        padding: '0.75rem 1rem',
        marginBottom: '0.5rem',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        outline: 'none',
        color: '#4b5563',
        minHeight: '120px',
        transition: 'border-color 0.3s, box-shadow 0.3s',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        backgroundColor: '#fafafa'
    };

    const errorStyle = {
        color: '#ef4444',
        fontSize: '0.75rem',
        marginBottom: '0.5rem',
        overflowWrap: 'break-word'
    };

    const submitButtonStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: '#f59e0b',
        color: '#fff',
        padding: '0.75rem 1.5rem',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        fontWeight: '500',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        width: '100%',
        justifyContent: 'center',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    };

    // Detailed job page styles
    const detailContainerStyle = {
        width: '100%',
        maxWidth: isMobile ? '100%' : (showApplicationForm ? '980px' : '1400px'), // Adjust maxWidth based on form visibility
        minWidth: isMobile ? '100%' : (showApplicationForm ? '700px' : '1000px'), // Adjust minWidth based on form visibility
        padding: isMobile ? '1rem' : '2rem',
        backgroundColor: '#fff',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '2rem',
        boxSizing: 'border-box'
    };

    const jobDetailsStyle = {
        flex: isMobile ? '1' : (showApplicationForm ? '1' : '0 0 70%'), // Full flex when form is open
        width: isMobile ? '100%' : (showApplicationForm ? '100%' : '70%'), // Full width when form is open
        paddingRight: isMobile ? '0' : (showApplicationForm ? '0' : '2rem') // Remove padding when form is open
    };

    const similarJobsContainerStyle = {
        flex: isMobile ? '1' : '0 0 30%',
        width: isMobile ? '100%' : '30%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        padding: '2rem',
        backgroundColor: 'transparent',
        borderRadius: '0.5rem',
        boxSizing: 'border-box'
    };

    const similarJobCardStyle = {
        backgroundColor: '#e5e7eb',
        padding: '1rem',
        borderRadius: '0.5rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        transition: 'box-shadow 0.3s, transform 0.3s',
        cursor: 'pointer',
        width: '100%',
        boxSizing: 'border-box'
    };

    const applicationFormStyle = {
        marginTop: '2rem',
        padding: '1.5rem',
        backgroundColor: '#f9fafb',
        borderRadius: '0.5rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginLeft: '0',
        width: '100%',
        boxSizing: 'border-box'
    };

    return (
        <div style={{ padding: isMobile ? '1rem' : '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Loading State */}
            {loading && (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '200px',
                    fontSize: '1.2rem',
                    color: '#6b7280'
                }}>
                    Loading job openings...
                </div>
            )}
            
            {/* Error State */}
            {error && !loading && (
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '200px',
                    padding: '2rem',
                    backgroundColor: '#fee2e2',
                    borderRadius: '0.5rem',
                    border: '1px solid #fecaca',
                    maxWidth: '500px',
                    margin: '2rem auto'
                }}>
                    <div style={{ fontSize: '1.2rem', color: '#dc2626', marginBottom: '1rem' }}>
                        ⚠️ {error}
                    </div>
                    <button 
                        onClick={fetchJobs}
                        style={{
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '0.5rem',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1rem'
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}
            
            {/* Main Content */}
            {!loading && !error && (
                <>
                    {!selectedJob ? (
                        <>
                            {/* Filter Section */}
                            <div style={filterSectionStyle}>
                        {/* Job Title Dropdown */}
                        <div style={dropdownContainerStyle}>
                            <select
                                style={dropdownStyle}
                                value={jobTitleFilter}
                                onChange={(e) => setJobTitleFilter(e.target.value)}
                                disabled={isTyping}
                            >
                                <option value="">Job Title</option>
                                {jobTitles.map((title, index) => (
                                    <option key={index} value={title}>{title}</option>
                                ))}
                            </select>
                            <svg
                                style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '1.25rem',
                                    height: '1.25rem',
                                    color: '#6b7280'
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>

                        {/* Job Type Dropdown */}
                        <div style={dropdownContainerStyle}>
                            <select
                                style={dropdownStyle}
                                value={jobTypeFilter}
                                onChange={(e) => setJobTypeFilter(e.target.value)}
                                disabled={isTyping}
                            >
                                <option value="">Job Type</option>
                                {jobTypes.map((type, index) => (
                                    <option key={index} value={type}>{type}</option>
                                ))}
                            </select>
                            <svg
                                style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '1.25rem',
                                    height: '1.25rem',
                                    color: '#6b7280'
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>

                        {/* Location Dropdown */}
                        <div style={dropdownContainerStyle}>
                            <select
                                style={dropdownStyle}
                                value={locationFilter}
                                onChange={(e) => setLocationFilter(e.target.value)}
                                disabled={isTyping}
                            >
                                <option value="">Location</option>
                                {locations.map((location, index) => (
                                    <option key={index} value={location}>{location}</option>
                                ))}
                            </select>
                            <svg
                                style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '1.25rem',
                                    height: '1.25rem',
                                    color: '#6b7280'
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>

                        {/* Search Input with Underline */}
                        <div style={searchContainerStyle}>
                            <input
                                type="text"
                                placeholder="Search jobs..."
                                style={searchInputStyle}
                                value={filter}
                                onChange={handleSearchChange}
                            />
                            <svg
                                style={{
                                    position: 'absolute',
                                    right: '0',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '1.25rem',
                                    height: '1.25rem',
                                    color: '#f59e0b'
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </div>
                    </div>

                    {/* Job Listings */}
                    {filteredJobs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem 0', width: '100%' }}>
                            <p style={{ fontSize: isMobile ? '1rem' : '1.25rem', color: '#4b5563', overflowWrap: 'break-word' }}>
                                No search results found.
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', maxWidth: isMobile ? '100%' : '1000px' }}>
                            {filteredJobs.map(job => (
                                <div
                                    key={job.id}
                                    style={jobCardStyle}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                                        e.currentTarget.style.transform = 'scale(1.02)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: '1rem' }}>
                                        <div style={{ flex: 1, minWidth: isMobile ? '100%' : '300px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <h3 style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 'bold', color: '#1f2937', overflowWrap: 'break-word' }}>
                                                    {job.title}
                                                </h3>
                                                <svg
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleJobSummary(job.id);
                                                    }}
                                                    style={{
                                                        width: '1.25rem',
                                                        height: '1.25rem',
                                                        cursor: 'pointer',
                                                        transform: expandedJobs[job.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.3s'
                                                    }}
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 9l-7 7-7-7"
                                                    />
                                                </svg>
                                            </div>
                                            <div style={{ marginTop: '0.5rem' }}>
                                                {expandedJobs[job.id] ? (
                                                    <>
                                                        <p style={{ fontSize: isMobile ? '0.9rem' : '1rem', color: '#4b5563', overflowWrap: 'break-word' }}>
                                                            {job.expandedDescription.line1}
                                                        </p>
                                                        <p style={{ fontSize: isMobile ? '0.9rem' : '1rem', color: '#4b5563', overflowWrap: 'break-word' }}>
                                                            {job.expandedDescription.line2}
                                                        </p>
                                                        <p style={{ fontSize: isMobile ? '0.9rem' : '1rem', color: '#4b5563', overflowWrap: 'break-word' }}>
                                                            {job.expandedDescription.line3}
                                                        </p>
                                                    </>
                                                ) : (
                                                    <p style={{ fontSize: isMobile ? '0.9rem' : '1rem', color: '#4b5563', overflowWrap: 'break-word' }}>
                                                        {job.intro}
                                                    </p>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    backgroundColor: '#f3f4f6',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    color: '#4b5563'
                                                }}>
                                                    <svg
                                                        style={{ width: '1rem', height: '1rem', color: '#f59e0b' }}
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    {job.type}
                                                </span>
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    backgroundColor: '#f3f4f6',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    color: '#4b5563'
                                                }}>
                                                    <svg
                                                        style={{ width: '1rem', height: '1rem', color: '#f59e0b' }}
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                    </svg>
                                                    {mapLocation(job.location)}
                                                </span>
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    backgroundColor: '#f3f4f6',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    color: '#4b5563'
                                                }}>
                                                    <span style={{ fontSize: '1rem', color: '#f59e0b' }}>₹</span>
                                                    {job.salary}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleApplyNow(job);
                                            }}
                                            style={buttonStyle}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#d97706')}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f59e0b')}
                                        >
                                            Apply Now
                                            <svg
                                                style={{ width: '1rem', height: '1rem', transform: 'rotate(-45deg)' }}
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload Resume Section */}
                    <div style={{ textAlign: 'center', padding: '2rem 0', width: '100%', maxWidth: isMobile ? '100%' : '1000px' }}>
                        <p style={{ fontSize: isMobile ? '1rem' : '1.25rem', color: '#4b5563', marginBottom: '1rem', overflowWrap: 'break-word' }}>
                            Didn't find the perfect role? Share your resume, and we'll reach out with future opportunities!
                        </p>
                        <button
                            onClick={openModal}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                backgroundColor: '#f59e0b',
                                color: '#fff',
                                padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1rem',
                                borderRadius: '0.5rem',
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s',
                                margin: '0 auto'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#d97706')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f59e0b')}
                        >
                            Upload Resume
                            <svg
                                style={{ width: '1rem', height: '1rem' }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Modal for Upload Resume */}
                    {showModal && (
                        <div style={modalOverlayStyle} onClick={closeModal}>
                            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                                <button style={closeButtonStyle} onClick={closeModal}>×</button>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem', textAlign: 'center' }}>
                                    Upload Your Resume
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Your Name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.name && <p style={errorStyle}>{errors.name}</p>}
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Your Email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.email && <p style={errorStyle}>{errors.email}</p>}
                                    <input
                                        type="file"
                                        name="resume"
                                        accept=".pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={handleFileChange}
                                        style={inputStyle}
                                    />
                                    {errors.resume && <p style={errorStyle}>{errors.resume}</p>}
                                    <button
                                        onClick={handleApplicationSubmit}
                                        style={submitButtonStyle}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#d97706')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f59e0b')}
                                        disabled={!!errors.name || !!errors.email || !!errors.resume || !formData.name || !formData.email || !formData.resume}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                // Detailed Job Page
                <div style={detailContainerStyle}>
                    {/* Job Details Section */}
                    <div style={jobDetailsStyle}>
                        {/* Back Button */}
                        <button
                            onClick={handleBackToListings}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                backgroundColor: '#e5e7eb',
                                color: '#4b5563',
                                padding: '0.5rem 1rem',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer',
                                marginBottom: '1rem'
                            }}
                        >
                            <svg
                                style={{ width: '1rem', height: '1rem', transform: 'rotate(180deg)' }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                                />
                            </svg>
                            Back to Listings
                        </button>

                        {/* Job Details */}
                        <h2 style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>
                            {selectedJob.title}
                        </h2>
                        <p style={{ fontSize: '1rem', color: '#4b5563', marginBottom: '1rem' }}>{selectedJob.intro}</p>
                        <div style={{ marginBottom: '1rem' }}>
                            <p style={{ fontSize: '1rem', color: '#4b5563' }}>{selectedJob.expandedDescription.line1}</p>
                            <p style={{ fontSize: '1rem', color: '#4b5563' }}>{selectedJob.expandedDescription.line2}</p>
                            <p style={{ fontSize: '1rem', color: '#4b5563' }}>{selectedJob.expandedDescription.line3}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                backgroundColor: '#f3f4f6',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.875rem',
                                color: '#4b5563'
                            }}>
                                <svg
                                    style={{ width: '1rem', height: '1rem', color: '#f59e0b' }}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                {selectedJob.type}
                            </span>
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                backgroundColor: '#f3f4f6',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.875rem',
                                color: '#4b5563'
                            }}>
                                <svg
                                    style={{ width: '1rem', height: '1rem', color: '#f59e0b' }}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                </svg>
                                {mapLocation(selectedJob.location)}
                            </span>
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                backgroundColor: '#f3f4f6',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.875rem',
                                color: '#4b5563'
                            }}>
                                <span style={{ fontSize: '1rem', color: '#f59e0b' }}>₹</span>
                                {selectedJob.salary}
                            </span>
                            <span
                                onClick={e => handleShareClick(selectedJob, e)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    backgroundColor: '#fffbe6',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '9999px',
                                    fontSize: '0.875rem',
                                    color: '#f59e0b',
                                    cursor: 'pointer',
                                    border: '1px solid #f59e0b'
                                }}
                            >
                                {shareIcon} Share this job
                            </span>
                        </div>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginTop: '1.5rem' }}>Responsibilities</h3>
                        <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem', color: '#4b5563' }}>
                            {selectedJob.responsibilities.map((resp, index) => (
                                <li key={index} style={{ marginBottom: '0.5rem' }}>{resp}</li>
                            ))}
                        </ul>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginTop: '1.5rem' }}>Requirements</h3>
                        <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem', color: '#4b5563' }}>
                            {selectedJob.requirements.map((req, index) => (
                                <li key={index} style={{ marginBottom: '0.5rem' }}>{req}</li>
                            ))}
                        </ul>

                        {/* Submit Application Button (Left-Aligned) */}
                        {!showApplicationForm && (
                            <button
                                onClick={openApplicationForm}
                                style={{
                                    ...buttonStyle,
                                    marginTop: '2rem',
                                    marginLeft: '0',
                                    width: isMobile ? '100%' : 'auto'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#d97706')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f59e0b')}
                            >
                                Submit Your Application
                                <svg
                                    style={{ width: '1rem', height: '1rem', transform: 'rotate(-45deg)' }}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                                    />
                                </svg>
                            </button>
                        )}

                        {/* Application Form */}
                        {showApplicationForm && (
                            <div style={applicationFormStyle}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937' }}>
                                        Apply for {selectedJob.title}
                                    </h3>
                                    <button onClick={closeApplicationForm} style={closeButtonStyle}>×</button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Your Name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.name && <p style={errorStyle}>{errors.name}</p>}
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Your Email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.email && <p style={errorStyle}>{errors.email}</p>}
                                    <input
                                        type="tel"
                                        name="phone"
                                        placeholder="Phone Number (e.g., +919876543210)"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.phone && <p style={errorStyle}>{errors.phone}</p>}
                                    <input
                                        type="url"
                                        name="linkedin"
                                        placeholder="LinkedIn Profile (optional)"
                                        value={formData.linkedin}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    <input
                                        type="text"
                                        name="experience"
                                        placeholder="Years of Experience (e.g., 2.5)"
                                        value={formData.experience}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.experience && <p style={errorStyle}>{errors.experience}</p>}
                                    <input
                                        type="text"
                                        name="currentLocation"
                                        placeholder="Current Location"
                                        value={formData.currentLocation}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.currentLocation && <p style={errorStyle}>{errors.currentLocation}</p>}
                                    <input
                                        type="text"
                                        name="noticePeriod"
                                        placeholder="Notice Period (in days)"
                                        value={formData.noticePeriod}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.noticePeriod && <p style={errorStyle}>{errors.noticePeriod}</p>}
                                    <textarea
                                        name="coverLetter"
                                        placeholder="Your Cover Letter"
                                        value={formData.coverLetter}
                                        onChange={handleInputChange}
                                        style={textareaStyle}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f59e0b';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    {errors.coverLetter && <p style={errorStyle}>{errors.coverLetter}</p>}
                                    <input
                                        type="file"
                                        name="resume"
                                        accept=".pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={handleFileChange}
                                        style={inputStyle}
                                    />
                                    {errors.resume && <p style={errorStyle}>{errors.resume}</p>}
                                    <button
                                        onClick={handleApplicationSubmit}
                                        style={submitButtonStyle}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#d97706')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f59e0b')}
                                        disabled={
                                            !!errors.name ||
                                            !!errors.email ||
                                            !!errors.phone ||
                                            !!errors.experience ||
                                            !!errors.currentLocation ||
                                            !!errors.noticePeriod ||
                                            !!errors.coverLetter ||
                                            !!errors.resume ||
                                            !formData.name ||
                                            !formData.email ||
                                            !formData.phone ||
                                            !formData.experience ||
                                            !formData.currentLocation ||
                                            !formData.noticePeriod ||
                                            !formData.coverLetter ||
                                            !formData.resume
                                        }
                                    >
                                        Submit Application
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Similar Jobs Sidebar */}
                    {!showApplicationForm && (
                        <div style={similarJobsContainerStyle}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>
                                Similar Jobs
                            </h3>
                            {currentJobData
                                .filter(job => job.id !== selectedJob.id && (job.type === selectedJob.type || job.location === selectedJob.location))
                                .map(job => (
                                    <div
                                        key={job.id}
                                        style={similarJobCardStyle}
                                        onClick={() => handleJobCardClick(job)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                                            e.currentTarget.style.transform = 'scale(1.02)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1f2937' }}>
                                                {job.title}
                                            </h4>
                                            <p style={{ fontSize: '0.875rem', color: '#4b5563' }}>{job.intro}</p>
                                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    backgroundColor: '#f3f4f6',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: '0.75rem',
                                                    color: '#4b5563'
                                                }}>
                                                    <svg
                                                        style={{ width: '0.875rem', height: '0.875rem', color: '#f59e0b' }}
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    {job.type}
                                                </span>
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    backgroundColor: '#f3f4f6',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: '0.75rem',
                                                    color: '#4b5563'
                                                }}>
                                                    <svg
                                                        style={{ width: '0.875rem', height: '0.875rem', color: '#f59e0b' }}
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                    </svg>
                                                    {mapLocation(job.location)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            )}
            </>
            )}
            
            {/* Share Modal */}
            {showShareModal && shareJob && (
                <div style={modalOverlayStyle} onClick={closeShareModal}>
                    <div style={{ ...modalContentStyle, maxWidth: 340, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <button style={closeButtonStyle} onClick={closeShareModal}>×</button>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>Share this job</h3>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', margin: '1.5rem 0' }}>
                            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getJobUrl(shareJob))}&quote=${encodeURIComponent(getShareMessage(shareJob))}`} target="_blank" rel="noopener noreferrer" title="Share on Facebook">{socialIcons.facebook}</a>
                            <a href={`https://wa.me/?text=${encodeURIComponent(getShareMessage(shareJob))}`} target="_blank" rel="noopener noreferrer" title="Share on WhatsApp">{socialIcons.whatsapp}</a>
                            <a href={`https://t.me/share/url?url=${encodeURIComponent(getJobUrl(shareJob))}&text=${encodeURIComponent(getShareMessage(shareJob))}`} target="_blank" rel="noopener noreferrer" title="Share on Telegram">{socialIcons.telegram}</a>
                            <a href={`mailto:?subject=${encodeURIComponent('Job Opportunity: ' + shareJob.title)}&body=${encodeURIComponent(getShareMessage(shareJob))}`} target="_blank" rel="noopener noreferrer" title="Share via Email">{socialIcons.email}</a>
                        </div>
                        <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Invite your friends or network to apply for this role!</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CareersBody;